import { useMemo, useState } from 'react';
import { useRefreshSchemaAudit, useSchemaAuditQuery } from '../../api/queries';
import { toCsv, downloadCsv } from '../../lib/csv';
import { showToast } from '../../components/Toast';
import type { SchemaPage } from '../../types';

type Props = { af: string };

/** The crawl is meant to run about once a quarter; past this we nudge. */
const STALE_AFTER_DAYS = 90;

function daysSince(iso: string | null): number | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86_400_000);
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

function todayStamp(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

function exportSchemaCsv(af: string, pages: SchemaPage[]): void {
  const headers = [
    'URL',
    'Schema Types',
    'Blocks',
    'HTTP Status',
    'Last Modified',
    'Error',
    'Raw JSON-LD',
  ];
  const rows = pages.map((p) => [
    p.url,
    p.types.join('; '),
    p.blockCount,
    p.httpStatus ?? '',
    p.lastmod ?? '',
    p.error ?? '',
    p.rawJsonLd ?? '',
  ]);
  downloadCsv(`schema_${af}_${todayStamp()}.csv`, toCsv(headers, rows));
}

/** Path + query of a URL, so the table isn't 47 rows of the same origin. */
function shortPath(url: string): string {
  try {
    const u = new URL(url);
    return (u.pathname + u.search) || '/';
  } catch {
    return url;
  }
}

export default function SchemaSubTab({ af }: Props) {
  const q = useSchemaAuditQuery(af, true);
  const refresh = useRefreshSchemaAudit(af);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [onlyMissing, setOnlyMissing] = useState(false);

  const pages = useMemo(() => q.data?.pages ?? [], [q.data]);

  // Type frequency across the site + the count of pages with no markup at all.
  const { typeCounts, missingCount } = useMemo(() => {
    const counts = new Map<string, number>();
    let missing = 0;
    for (const p of pages) {
      if (p.types.length === 0 && !p.error) missing += 1;
      for (const t of p.types) counts.set(t, (counts.get(t) ?? 0) + 1);
    }
    return {
      typeCounts: [...counts.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])),
      missingCount: missing,
    };
  }, [pages]);

  const visible = onlyMissing ? pages.filter((p) => p.types.length === 0) : pages;

  function runRefresh() {
    refresh.mutate(undefined, {
      onSuccess: (fresh) => {
        showToast(`Schema refreshed — ${fresh.pages.length} URLs crawled.`);
        setExpanded(null);
      },
      onError: (err) => showToast(`Refresh failed: ${err.message}`, { variant: 'error' }),
    });
  }

  if (q.isLoading) {
    return (
      <div className="listings-loading">
        <div className="spinner" />
        Loading schema audit…
      </div>
    );
  }

  if (q.error) {
    return (
      <div className="tab-placeholder">
        <h3>Schema Unavailable</h3>
        <p>{(q.error as Error).message}</p>
      </div>
    );
  }

  const crawledAt = q.data?.crawledAt ?? null;
  const age = daysSince(crawledAt);
  const isStale = age !== null && age > STALE_AFTER_DAYS;
  const busy = refresh.isPending;

  // Never crawled — offer the first run rather than showing an empty table.
  if (!crawledAt) {
    return (
      <div className="tab-placeholder">
        <h3>No schema audit yet</h3>
        <p>
          Crawl this franchisee's sitemap to list every URL on the site and the JSON-LD
          schema each page emits. Takes a few seconds.
        </p>
        <button type="button" className="schema-refresh-btn" onClick={runRefresh} disabled={busy}>
          {busy ? 'Crawling…' : 'Run first crawl'}
        </button>
      </div>
    );
  }

  return (
    <>
      <div className="schema-header">
        <div className="schema-meta">
          <span className="schema-count">
            {pages.length} URL{pages.length === 1 ? '' : 's'}
          </span>
          <span className="schema-crawled">
            Last crawled {fmtDate(crawledAt)}
            {age !== null && ` (${age === 0 ? 'today' : `${age} day${age === 1 ? '' : 's'} ago`})`}
          </span>
          {isStale && <span className="schema-stale-badge">Due for refresh</span>}
        </div>
        <div className="schema-actions">
          <button type="button" className="schema-refresh-btn" onClick={runRefresh} disabled={busy}>
            {busy ? 'Crawling…' : '↻ Refresh'}
          </button>
          <button
            type="button"
            className="export-csv-btn admin-only"
            onClick={() => exportSchemaCsv(af, pages)}
            disabled={pages.length === 0}
          >
            ⬇ Download CSV
          </button>
        </div>
      </div>

      {q.data?.errorMessage && <div className="schema-warning">{q.data.errorMessage}</div>}

      {pages.length > 0 && (
        <div className="schema-summary">
          <button
            type="button"
            className={`schema-chip schema-chip-missing${onlyMissing ? ' is-active' : ''}`}
            onClick={() => setOnlyMissing((v) => !v)}
            title="Show only pages with no structured data"
          >
            No schema <strong>{missingCount}</strong>
          </button>
          {typeCounts.map(([type, n]) => (
            <span key={type} className="schema-chip">
              {type} <strong>{n}</strong>
            </span>
          ))}
        </div>
      )}

      <div className="calls-table">
        <table>
          <thead>
            <tr>
              <th>URL</th>
              <th>Schema Types</th>
              <th style={{ width: 70 }}>Blocks</th>
              <th style={{ width: 80 }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((p) => {
              const isOpen = expanded === p.url;
              const hasSchema = p.types.length > 0;
              return [
                <tr key={p.url} className={isOpen ? 'schema-row is-open' : 'schema-row'}>
                  <td>
                    <button
                      type="button"
                      className="schema-expand"
                      onClick={() => setExpanded(isOpen ? null : p.url)}
                      disabled={!p.rawJsonLd}
                      title={p.rawJsonLd ? 'Show raw JSON-LD' : 'No JSON-LD to show'}
                    >
                      {p.rawJsonLd ? (isOpen ? '▾' : '▸') : '·'}
                    </button>
                    <a href={p.url} target="_blank" rel="noreferrer" title={p.url}>
                      {shortPath(p.url)}
                    </a>
                  </td>
                  <td>
                    {hasSchema ? (
                      p.types.map((t) => (
                        <span key={t} className="schema-type-tag">
                          {t}
                        </span>
                      ))
                    ) : (
                      <span className="schema-none">{p.error ? p.error : 'No structured data'}</span>
                    )}
                  </td>
                  <td>{p.blockCount}</td>
                  <td>
                    <span
                      className={p.httpStatus === 200 ? 'schema-status-ok' : 'schema-status-bad'}
                    >
                      {p.httpStatus ?? '—'}
                    </span>
                  </td>
                </tr>,
                isOpen && p.rawJsonLd ? (
                  <tr key={`${p.url}-raw`} className="schema-raw-row">
                    <td colSpan={4}>
                      <pre className="schema-raw">{p.rawJsonLd}</pre>
                    </td>
                  </tr>
                ) : null,
              ];
            })}
          </tbody>
        </table>
      </div>

      {visible.length === 0 && (
        <div className="tab-placeholder">
          <p>No pages match this filter.</p>
        </div>
      )}

      <div className="metrics-footer">
        Structured data (JSON-LD) discovered via {q.data?.sitemapUrl ?? 'the site sitemap'} • Admin
        only
      </div>
    </>
  );
}
